import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

function DashboardExterior({ getAuthHeaders }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const response = await axios.get(`${API}/dashboard-exterior/estatisticas`, getAuthHeaders());
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  if (!stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard Exterior</h1>

      {/* Cards Superiores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Card Fixed Income */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-emerald-100">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Fixed Income</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_fixed_income)}</p>
              <p className="text-xs text-gray-500 mt-1">Renda Fixa Exterior</p>
            </div>
          </div>
        </div>

        {/* Card REIT's */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">REIT's</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_reits)}</p>
              <p className="text-xs text-gray-500 mt-1">Fundos Imobiliários</p>
            </div>
          </div>
        </div>

        {/* Card Business */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-cyan-100">
              <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Business</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_business)}</p>
              <p className="text-xs text-gray-500 mt-1">Empresas Internacionais</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Coluna 1: Gráfico Business */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Business – Evolução e Metas</h2>
          
          {stats.business.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhuma empresa Business cadastrada</p>
          ) : (
            <div className="space-y-4">
              {stats.business.map((item) => (
                <div key={item.codigo}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{item.codigo}</span>
                    <span className="text-xs text-gray-500">{item.percentual_meta.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-cyan-500 to-cyan-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(item.percentual_meta, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    {item.meta_tipo === 'quantidade' ? (
                      <>
                        <span className="text-xs text-gray-500">{item.quantidade_total} ações</span>
                        <span className="text-xs text-gray-500">Meta: {item.meta} ações</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-gray-500">{formatCurrency(item.valor_investido)}</span>
                        <span className="text-xs text-gray-500">Meta: {formatCurrency(item.meta)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Coluna 2: Gráfico REIT's */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h2 className="text-lg font-bold text-gray-900 mb-4">REIT's – Evolução e Metas</h2>
          
          {stats.reits.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhum REIT cadastrado</p>
          ) : (
            <div className="space-y-4">
              {stats.reits.map((item) => (
                <div key={item.codigo}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700">{item.codigo}</span>
                    <span className="text-xs text-gray-500">{item.percentual_meta.toFixed(1)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-orange-600 h-3 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(item.percentual_meta, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    {item.meta_tipo === 'quantidade' ? (
                      <>
                        <span className="text-xs text-gray-500">{item.quantidade_total} ações</span>
                        <span className="text-xs text-gray-500">Meta: {item.meta} ações</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-gray-500">{formatCurrency(item.valor_investido)}</span>
                        <span className="text-xs text-gray-500">Meta: {formatCurrency(item.meta)}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardExterior;
